import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_LOADER_ID, GOOGLE_MAPS_LIBRARIES } from '@/lib/googleMapsLoader';

type GoogleApi = typeof google;

let useGoogleMaps = false;
let apiKey = '';
let loadPromise: Promise<GoogleApi> | null = null;
let loaded = false;
const useListeners = new Set<(value: boolean) => void>();
const keyListeners = new Set<(value: string) => void>();
const loadedListeners = new Set<(value: boolean) => void>();

const originalLoad: Loader['load'] = Loader.prototype.load;

Loader.prototype.load = function loadInterceptor(this: Loader): ReturnType<Loader['load']> {
  if (useGoogleMaps) {
    return originalLoad.call(this);
  }
  return Promise.reject(new Error('Google Maps API is disabled.'));
};

async function loadScript(): Promise<GoogleApi> {
  const key = apiKey;
  if (!key) {
    throw new Error('Google Maps API key is not configured.');
  }

  const loader = new Loader({
    apiKey: key,
    id: GOOGLE_MAPS_LOADER_ID,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: 'weekly',
  });

  const googleApi = await loader.load();
  loaded = true;
  loadedListeners.forEach((fn) => fn(true));
  return googleApi;
}

export const googleMapsApiSettings = {
  getUseGoogleMaps(): boolean {
    return useGoogleMaps;
  },
  setUseGoogleMaps(value: boolean): boolean {
    if (useGoogleMaps === value) {
      return useGoogleMaps;
    }
    useGoogleMaps = value;
    useListeners.forEach((fn) => fn(useGoogleMaps));
    return useGoogleMaps;
  },
  getApiKey(): string {
    return apiKey;
  },
  setApiKey(value: string): void {
    if (apiKey === value) {
      return;
    }
    apiKey = value;
    keyListeners.forEach((fn) => fn(apiKey));
  },
  toggle(): boolean {
    return this.setUseGoogleMaps(!useGoogleMaps);
  },
  subscribe(fn: (value: boolean) => void): () => void {
    useListeners.add(fn);
    fn(useGoogleMaps);
    return () => useListeners.delete(fn);
  },
  subscribeApiKey(fn: (value: string) => void): () => void {
    keyListeners.add(fn);
    fn(apiKey);
    return () => keyListeners.delete(fn);
  },
  subscribeLoaded(fn: (value: boolean) => void): () => void {
    loadedListeners.add(fn);
    fn(loaded);
    return () => loadedListeners.delete(fn);
  },
  isLoaded(): boolean {
    return loaded;
  },
  async load(): Promise<GoogleApi> {
    if (loadPromise) {
      return loadPromise;
    }
    loadPromise = loadScript().catch((err) => {
      loadPromise = null;
      throw err;
    });
    return loadPromise;
  },
};
